import { Toast } from "primereact/toast";
import { useRef, useState, useEffect } from "react";
import { FaArrowLeft, FaChalkboardTeacher, FaVideo, FaPhone, FaClock, FaArrowRight } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import classNames from 'classnames';
import { motion, AnimatePresence } from "framer-motion";
import { analytics } from "../features/firebase/firebase-config";

export const InterviewTypeLabelMapping: { [key: string]: string } = {
    'video-interview': 'Video Interview',
    'phone-interview': 'Phone Interview',
    'whiteboard-session': 'Whiteboard Session',
    'public-speaking': 'Public Speaking Practice',
    'timed-pitch': 'Timed Pitch Challenge',
};

export interface InterviewType {
    id: number;
    name: string;
    value: string;
    description: string;
    icon: React.ReactElement;
}

export const interviewTypes: InterviewType[] = [
    {
        id: 1,
        name: 'Video Call Interview',
        value: 'video-interview',
        description: 'Conduct video interview practice sessions',
        icon: <FaVideo />,
    },
    {
        id: 2,
        name: 'Phone Call Interview',
        value: 'phone-interview',
        description: 'Conduct phone interview practice sessions',
        icon: <FaPhone />,
    },
    {
        id: 6,
        name: 'Timed Pitch Challenge',
        value: 'timed-pitch',
        description: 'Assess your performance speaking on a topic under time constraints',
        icon: <FaClock />,
    },
    {
        id: 4,
        name: 'Whiteboard Session',
        value: 'whiteboard-session',
        description: 'Assess your performance speaking on a topic under time constraints with your favorite whiteboards for illustrations',
        icon: <FaChalkboardTeacher />,
    },
];

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1
        }
    }
};

const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { type: 'spring', damping: 12 }
    }
};

const buttonVariants = {
    initial: { scale: 1 },
    hover: { scale: 1.05, boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.1)" },
    selected: {
        scale: 1.05,
        boxShadow: "0px 8px 20px rgba(79, 70, 229, 0.2)",
        transition: { duration: 0.3 }
    },
    tap: { scale: 0.98 }
};

export default function NewSessionTypeSelectionPage() {
    analytics.logEvent('page_view', {
        page_title: 'new session type select',
    });
    const toast = useRef<Toast>(null);
    const navigate = useNavigate();
    const [selectedType, setSelectedType] = useState<number | null>(null);
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        // Animation timing setup
        const timeout = setTimeout(() => setIsReady(true), 100);
        return () => clearTimeout(timeout);
    }, []);

    const handleSelection = (id: number) => {
        setSelectedType(prev => (prev === id ? null : id));
    };

    const handleSubmit = () => {
        if (selectedType === null) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Selection Required',
                detail: 'Please select an interview type.'
            });
            return;
        }
        const selected = interviewTypes.find(type => type.id === selectedType);
        navigate(`/new-session?type=${selected?.value}`);
    };

    const handleBackClick = () => {
        navigate(-1);
    };

    return (
        <div className="min-h-screen flex flex-col bg-gradient-to-b from-blue-50 to-indigo-50">
            <Toast ref={toast} position="top-center" />

            <header className="flex justify-between items-center p-4 sm:p-6 bg-white/80 backdrop-blur shadow-sm sticky top-0 z-10">
                <button
                    onClick={handleBackClick}
                    className="flex items-center text-indigo-600 hover:text-indigo-800 transition px-3 py-2 rounded-lg hover:bg-indigo-50"
                    aria-label="Go back"
                >
                    <FaArrowLeft className="mr-2" />
                    <span className="hidden sm:inline">Back</span>
                </button>

                <div className="text-sm text-gray-500">
                    <span className="hidden sm:inline">Step</span> 1 of 2
                </div>
            </header>

            <main className="flex-1 flex flex-col items-center py-8 px-4 sm:py-12 sm:px-6 md:px-8 max-w-7xl mx-auto w-full">
                <motion.div
                    className="text-center mb-10"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <motion.h1
                        className="text-3xl md:text-4xl font-bold text-gray-800 mb-3"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        Select Your Session Type
                    </motion.h1>
                    <motion.p
                        className="text-gray-600 max-w-xl mx-auto"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                    >
                        Choose the type of interview practice session you'd like to conduct
                    </motion.p>
                </motion.div>

                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate={isReady ? "visible" : "hidden"}
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 w-full"
                >
                    {interviewTypes.map(type => {
                        const isSelected = selectedType === type.id;
                        return (
                            <motion.div
                                key={type.id}
                                variants={cardVariants}
                                whileHover="hover"
                                whileTap="tap"
                                className="h-full"
                            >
                                <motion.button
                                    onClick={() => handleSelection(type.id)}
                                    aria-pressed={isSelected}
                                    variants={buttonVariants}
                                    initial="initial"
                                    animate={isSelected ? "selected" : "initial"}
                                    className={classNames(
                                        'w-full h-full flex flex-col items-center p-6 rounded-xl transition transform focus:outline-none border-2',
                                        {
                                            'bg-indigo-600 text-white border-indigo-700': isSelected,
                                            'bg-white text-gray-800 border-gray-200 hover:border-indigo-300': !isSelected,
                                        }
                                    )}
                                >
                                    <div className={classNames(
                                        "text-4xl mb-5 p-4 rounded-full",
                                        {
                                            'bg-indigo-500 text-white': isSelected,
                                            'bg-indigo-100 text-indigo-600': !isSelected,
                                        }
                                    )}>
                                        {type.icon}
                                    </div>
                                    <h2 className="text-xl font-semibold mb-3">{type.name}</h2>
                                    <p className={classNames(
                                        "text-center text-sm",
                                        {
                                            'text-indigo-100': isSelected,
                                            'text-gray-600': !isSelected,
                                        }
                                    )}>
                                        {type.description}
                                    </p>

                                    <AnimatePresence>
                                        {isSelected && (
                                            <motion.div
                                                initial={{ opacity: 0, scale: 0.8 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0, scale: 0.8 }}
                                                className="mt-4 flex items-center text-white bg-indigo-500 px-3 py-1 rounded-full text-xs font-medium"
                                            >
                                                Selected
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.button>
                            </motion.div>
                        );
                    })}
                </motion.div>

                <motion.div
                    className="w-full mt-12 flex justify-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                >
                    <motion.button
                        onClick={handleSubmit}
                        disabled={selectedType === null}
                        whileHover={selectedType !== null ? { scale: 1.05 } : {}}
                        whileTap={selectedType !== null ? { scale: 0.98 } : {}}
                        className={classNames(
                            'px-8 py-4 rounded-xl text-lg font-semibold transition transform focus:outline-none shadow-md flex items-center justify-center min-w-[200px]',
                            {
                                'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg cursor-pointer': selectedType !== null,
                                'bg-gray-300 text-gray-500 cursor-not-allowed': selectedType === null,
                            }
                        )}
                    >
                        Continue
                        {selectedType !== null && <FaArrowRight className="ml-2" />}
                    </motion.button>
                </motion.div>
            </main>

            <footer className="py-4 px-6 text-center text-sm text-gray-500">
                <p>All interview sessions are recorded for your review and improvement</p>
            </footer>
        </div>
    );
}
